/**
 * EmployeeAutocompleteInput Component
 * 
 * A reusable autocomplete input component for searching and selecting office employees.
 * Provides debounced search functionality with keyboard navigation support.
 * 
 * Features:
 * - Debounced search (300ms delay) to reduce API calls
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - Suggestion filtering and sorting (exact code matches first, then name matches)
 * - Loading state indication
 * - Click-to-select functionality
 * - Shows only user account employees (4-digit codes) for office payslip
 * - Displays employee code and name
 * 
 * @component
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import EmployeeService from '../../../../services/EmployeeService';
import './EmployeeAutocompleteInput.css';

/**
 * Employee interface for internal use.
 * @interface
 */
interface Employee {
  empCode: string;  // Employee code
  name: string;      // Employee name
  userType: string;  // User type
}

/**
 * EmployeeSuggestion interface for autocomplete suggestions.
 * @interface
 */
interface EmployeeSuggestion {
  empCode: string;     // Employee code
  name: string;        // Employee name
  displayText: string; // Formatted display text (e.g., "EMP001 - John Doe")
  searchText: string;  // Searchable text (code + name for matching)
}

/**
 * Props interface for EmployeeAutocompleteInput component.
 */
interface EmployeeAutocompleteInputProps {
  value: string;                              // Current input value (employee code)
  onChange: (value: string) => void;          // Callback when input value changes
  placeholder?: string;                       // Placeholder text (default: "従業員コードまたは名前を入力")
  disabled?: boolean;                         // Whether input is disabled (default: false)
  className?: string;                         // Additional CSS classes
  id?: string;                                // Input element ID (default: "emp-code")
  onSelect?: (empCode: string, name: string) => void; // Callback when a suggestion is selected
}

/**
 * EmployeeAutocompleteInput Component
 * 
 * Provides autocomplete functionality for office employee code/name input.
 * Fetches suggestions from EmployeeService and filters them based on user input.
 * 
 * @param props - Component props
 * @returns React functional component
 */
export const EmployeeAutocompleteInput: React.FC<EmployeeAutocompleteInputProps> = ({
  value,
  onChange,
  placeholder = "従業員コードまたは名前を入力",
  disabled = false,
  className = "",
  id = "emp-code",
  onSelect
}) => {
  /**
   * State management:
   * - suggestions: Array of suggestion objects to display
   * - showSuggestions: Whether to show the suggestions dropdown
   * - isLoading: Whether a search is currently in progress
   * - selectedIndex: Index of currently highlighted suggestion (-1 for none)
   * - allEmployees: Cached list of all employees (for initial load)
   */
  const [suggestions, setSuggestions] = useState<EmployeeSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  
  /**
   * Refs for DOM element access:
   * - inputRef: Reference to the input element
   * - suggestionsRef: Reference to the suggestions dropdown container
   * - debounceTimeoutRef: Reference to the debounce timeout for cleanup
   */
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Fetch all employees on component mount (user account employees only, 4-digit codes).
   * Office payslip is linked by account name; only employees with accounts are shown.
   */
  useEffect(() => {
    const fetchAllEmployees = async () => {
      try {
        const userEmployeesResponse = await EmployeeService.getAllEmployees();
        const allEmployeesList: Employee[] = [];
        if (userEmployeesResponse.success && userEmployeesResponse.data) {
          allEmployeesList.push(...userEmployeesResponse.data);
        }
        setAllEmployees(allEmployeesList);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };

    fetchAllEmployees();
  }, []);

  /**
   * Creates searchable suggestions from employee list.
   * 
   * Formats employee data into suggestion objects with:
   * - Display text: "EMP001 - John Doe" format
   * - Search text: Lowercase combination of code and name for matching
   * 
   * @param employees - Array of Employee objects
   * @returns Array of EmployeeSuggestion objects
   */
  const createSuggestions = useCallback((employees: Employee[]): EmployeeSuggestion[] => {
    return employees.map(employee => ({
      empCode: employee.empCode,
      name: employee.name,
      displayText: `${employee.empCode} - ${employee.name}`,
      searchText: `${employee.empCode} ${employee.name}`.toLowerCase() // For case-insensitive matching
    }));
  }, []);

  /**
   * Debounced search: user account employees only (4-digit codes), sorted by relevance.
   *
   * @param searchTerm - Search term to filter employees by (code or name)
   */
  const debouncedSearch = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      const userEmployeesResponse = await EmployeeService.searchEmployees(searchTerm);
      const allEmployeesList: Employee[] = [];
      if (userEmployeesResponse.success && userEmployeesResponse.data) {
        allEmployeesList.push(...userEmployeesResponse.data);
      }
      
      // Create suggestions from search results
      const employeeSuggestions = createSuggestions(allEmployeesList);
      
      /**
       * Sort by relevance:
       * 1. Exact code match (highest priority)
       * 2. Code starts with search term (second priority)
       * 3. Name starts with search term (third priority)
       * 4. Alphabetical order by code (lowest priority)
       */
      const sortedSuggestions = employeeSuggestions.sort((a, b) => {
        const searchLower = searchTerm.toLowerCase();
        
        // Exact code match gets highest priority
        if (a.empCode.toLowerCase() === searchLower) return -1;
        if (b.empCode.toLowerCase() === searchLower) return 1;
        
        // Code starts with search term gets second priority
        if (a.empCode.toLowerCase().startsWith(searchLower) && !b.empCode.toLowerCase().startsWith(searchLower)) return -1;
        if (b.empCode.toLowerCase().startsWith(searchLower) && !a.empCode.toLowerCase().startsWith(searchLower)) return 1;
        
        // Name starts with search term gets third priority
        if (a.name.toLowerCase().startsWith(searchLower) && !b.name.toLowerCase().startsWith(searchLower)) return -1;
        if (b.name.toLowerCase().startsWith(searchLower) && !a.name.toLowerCase().startsWith(searchLower)) return 1;
        
        // Alphabetical order by code for everything else
        return a.empCode.localeCompare(b.empCode);
      });
      
      // Limit to 10 suggestions for better UX
      setSuggestions(sortedSuggestions.slice(0, 10));
      setShowSuggestions(sortedSuggestions.length > 0);
    } catch (error) {
      console.error('Error filtering employee suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, [createSuggestions]);

  /**
   * Handles input value changes with debouncing.
   * 
   * Updates the input value immediately and triggers a debounced search
   * after 300ms of no typing to reduce API calls.
   * 
   * @param e - Change event from input element
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Clear previous timeout if user is still typing
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set new timeout for debounced search (300ms delay)
    debounceTimeoutRef.current = setTimeout(() => {
      debouncedSearch(newValue);
    }, 300);
  };

  /**
   * Handles suggestion selection from dropdown.
   * 
   * Updates the input value with employee code, hides suggestions, and calls onSelect callback.
   * 
   * @param suggestion - Selected suggestion object
   */
  const handleSuggestionSelect = (suggestion: EmployeeSuggestion) => {
    onChange(suggestion.empCode);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    if (onSelect) {
      onSelect(suggestion.empCode, suggestion.name);
    }
  };

  /**
   * Handles keyboard navigation within suggestions dropdown.
   * 
   * Supports:
   * - ArrowDown: Move to next suggestion
   * - ArrowUp: Move to previous suggestion (or -1 if at top)
   * - Enter: Select highlighted suggestion
   * - Escape: Close suggestions dropdown
   * 
   * @param e - Keyboard event from input element
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Only handle keyboard navigation if suggestions are visible
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        // Move to next suggestion, or stay at last if already at end
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        // Move to previous suggestion, or -1 if at first
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        // Select highlighted suggestion if valid index
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        // Close suggestions dropdown
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  /**
   * Handles input focus event.
   * 
   * Shows suggestions dropdown if input has value and suggestions are available.
   */
  const handleFocus = () => {
    if (value.length > 0 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  /**
   * Handles input blur event.
   * 
   * Hides suggestions dropdown after a short delay to allow clicking on suggestions.
   * Uses 150ms delay to ensure click events on suggestions are processed before hiding.
   * 
   * @param e - Blur event from input element
   */
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      // Only hide if focus didn't move to suggestions dropdown
      if (!suggestionsRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    }, 150);
  };

  /**
   * Cleanup debounce timeout on component unmount.
   * 
   * Prevents memory leaks by clearing any pending timeouts when component is destroyed.
   */
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Hide suggestions when value changes externally (e.g., cleared programmatically).
   * 
   * Resets suggestions state when input value is cleared to ensure UI consistency.
   */
  useEffect(() => {
    if (value === '') {
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  }, [value]);

  return (
    <div className="autocomplete-container">
      {/* Input field with autocomplete functionality */}
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={`emp-code-input ${className} ${disabled ? 'disabled-input' : ''}`}
        autoComplete="off"
      />
      
      {/* Suggestions dropdown - only shown when showSuggestions is true */}
      {showSuggestions && (
        <div 
          ref={suggestionsRef}
          className="suggestions-dropdown"
        >
          {/* Loading state */}
          {isLoading ? (
            <div className="suggestion-item loading">
              <span>検索中...</span>
            </div>
          ) : suggestions.length > 0 ? (
            /**
             * Render suggestion list.
             * Each suggestion displays employee code and name.
             * Highlights selected item and updates selection on mouse hover.
             */
            suggestions.map((suggestion, index) => (
              <div
                key={`${suggestion.empCode}-${suggestion.name}`}
                className={`suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
                onClick={() => handleSuggestionSelect(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="suggestion-content">
                  <span className="emp-code">{suggestion.empCode}</span>
                  <span className="separator">-</span>
                  <span className="emp-name">{suggestion.name}</span>
                </div>
              </div>
            ))
          ) : (
            /* No results message */
            <div className="suggestion-item no-results">
              <span>該当する従業員が見つかりません</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
